import {
  Button,
  CuiLookup,
  type CuiLookupCompany,
  Drawer,
  Input,
  Select,
  Text,
  Textarea,
  toast,
} from '@pcrm/fe/ui-kit';
import {
  DOCUMENT_FILE_ACCEPT,
  isAcceptedDocumentFile,
  MAX_DOCUMENT_FILE_BYTES,
} from '@pcrm/shared/files';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Field } from '../../components/field';
import { ApiError } from '../../lib/api';
import { sessionQueryOptions } from '../auth/session';
import { lookupCompanyByCui } from '../companies/anaf.api';
import { companiesQueryOptions, companyDisplayName } from '../companies/companies.api';
import { INBOX_QUERY_KEY } from '../inbox/inbox.api';
import { partnerInputFromAnaf } from '../partners/partner-from-anaf';
import { ensurePartner, PARTNERS_QUERY_KEY } from '../partners/partners.api';
import { nomenclatorQueryOptions } from '../settings/settings.api';
import {
  DOCUMENT_NOTES_MAX_LENGTH,
  DOCUMENTS_QUERY_KEY,
  type DocumentListItem,
  type UploadOwner,
  uploadDocument,
} from './documents.api';
import { type DocumentOwner, OwnerPicker } from './owner-picker';

/**
 * Stările în care poate intra un act **primit**.
 *
 * `trimis` lipsește, și nu din uitare: starea aia înseamnă „a plecat un link de semnare și
 * clientul îl are pe telefon". Un contract adus pe hârtie n-a plecat nicăieri de la noi.
 * `anulat` nu se alege la naștere — s-ar consuma un număr pe un act mort din prima clipă.
 * Aceeași listă ca la atribuirea din căsuță; regula e a serverului
 * (`RECEIVED_DOCUMENT_STATES`).
 */
const STATE_LIST: readonly { value: 1 | 3; label: string }[] = [
  { value: 3, label: 'Semnat' },
  { value: 1, label: 'Nesemnat' },
];

/** Azi, `YYYY-MM-DD`, în ora locală — nu `toISOString`, care dă ziua de la Greenwich. */
function today(): string {
  const now = new Date();

  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

/**
 * Ce se opune trimiterii fișierului ăstuia, dacă ceva.
 *
 * ⚠️ **A doua plasă, nu singura.** `accept` filtrează doar fereastra de alegere și se
 * ocolește cu o tragere cu mouse-ul; regula care contează e pe server
 * (`DocumentFileInvalidError`). Verificarea de aici scutește operatorul de un drum dus-întors
 * de 25 MB ca să afle că a nimerit o arhivă.
 *
 * ⚠️ **Exportată, ca sertarul de reziliere să n-o copieze** (#140): acolo se urcă actul de
 * încetare, cu aceleași reguli de fișier. Două copii ar fi divergat la primul tip de fișier
 * adăugat, iar unul dintre ecrane ar fi refuzat ce celălalt primește.
 */
export function fileProblem(file: File | null): string | undefined {
  if (file === null) {
    return undefined;
  }

  if (file.size === 0) {
    return 'Fișierul e gol.';
  }

  if (file.size > MAX_DOCUMENT_FILE_BYTES) {
    return `Fișierul trece de ${Math.round(MAX_DOCUMENT_FILE_BYTES / (1024 * 1024))} MB.`;
  }

  return isAcceptedDocumentFile(file.name, file.type)
    ? undefined
    : 'Se primesc doar PDF-uri și poze (JPEG, PNG, HEIC, WebP, TIFF).';
}

/**
 * Proprietarul pe care ecranul îl **știe deja**, când îl știe.
 *
 * Trei intrări, un singur formular: sertarul de pe fișa clientului (clientul e dat), pagina
 * „Documente → Încarcă documente" (se alege) și, de la
 * [#139](https://github.com/bplugaru/efx-pcrm/issues/139), sertarul de pe rândul unui
 * partener din Conta → Parteneri (partenerul e dat).
 *
 * ⚠️ **Denumirea partenerului se poartă, nu se cere de la server.** Ecranul care deschide
 * sertarul are rândul în față; o a doua cerere doar ca să scrie un nume ar fi însemnat și o
 * rută nouă — `/partners/:id` nu există dinadins (vezi `PartnersController`).
 */
export type FixedUploadOwner =
  | { readonly kind: 'customer'; readonly customerId: string }
  | { readonly kind: 'partner'; readonly partnerId: string; readonly partnerName: string };

/**
 * Formularul de încărcare a unui act primit de la un terț.
 *
 * **O singură componentă pentru trei ecrane** — sertarul din tabul „Documente" al fișei,
 * pagina „Documente → Încarcă documente" și, de la
 * [#139](https://github.com/bplugaru/efx-pcrm/issues/139), sertarul de pe rândul unui partener
 * din Conta → Parteneri. Pe fișă și pe rândul partenerului proprietarul e deja știut, pe
 * pagină se alege. Copii ar fi divergat la prima regulă nouă, iar regula e chiar cea care
 * consumă un număr din registru.
 *
 * ## Client sau partener
 *
 * De la [#133](https://github.com/bplugaru/efx-pcrm/issues/133), pagina de încărcare are un
 * comutator: actul poate fi al unui **client** sau al unui **partener** al contabilității.
 * „Partenerul e partener, diferit de client. trebuie să fie clar asta" (fondatorul,
 * 2026-09-17) — de aceea sunt două rânduri distincte în formular, nu o listă comună în care
 * n-ai ști ce ai ales.
 *
 * Sertarele **nu** au comutatorul: acolo proprietarul e dat de ecranul care le-a deschis.
 *
 * ## Firma: de la client, sau aleasă
 *
 * ⚠️ **La un client, firma nu se alege**, deși tichetul
 * [#108](https://github.com/bplugaru/efx-pcrm/issues/108) o cerea în formular. Un client
 * aparține unei singure firme din grup, iar actul intră în registrul **ei** — un selector ar
 * fi lăsat operatorul să pună actul clientului A în registrul firmei B, iar cheia străină
 * n-ar fi avut nimic de obiectat.
 *
 * ⚠️ **La un partener firma se alege, și trebuie să se aleagă.** Partenerii sunt comuni
 * grupului, deci nu există firmă de moștenit. Selectorul pornește pe firma activă, dar
 * rămâne **vizibil și comutabil**: `architecture/tenancy.md` interzice explicit luarea ei
 * tăcute din sesiune — *„un operator care uită să comute ar consuma numere din registrul
 * altei firme"*, iar numărul nu se mai întoarce.
 */
export function UploadDocumentForm({
  fixedOwner = null,
  onUploaded,
  onCancel,
}: {
  /** Proprietarul, când ecranul îl știe deja. `null` = se alege în formular. */
  fixedOwner?: FixedUploadOwner | null;
  onUploaded?: (document: DocumentListItem) => void;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const documentTypes = useQuery(nomenclatorQueryOptions('tipuri-document'));
  const session = useQuery(sessionQueryOptions());
  const companies = useQuery(companiesQueryOptions());

  const [file, setFile] = useState<File | null>(null);
  /** Al cui e actul, când se alege în formular. `null` la un ecran cu proprietarul dat. */
  const [owner, setOwner] = useState<DocumentOwner | null>(null);
  const [partnerCui, setPartnerCui] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [documentState, setDocumentState] = useState<1 | 3>(3);
  const [documentDate, setDocumentDate] = useState(today);
  /** Mențiunile operatorului despre act — opționale (#137). */
  const [notes, setNotes] = useState('');
  /**
   * Numărul de registru, **doar pentru actele din sistemul vechi** (#239). Gol = următorul
   * număr automat, ca până acum.
   */
  const [documentNumber, setDocumentNumber] = useState('');
  /** Ce a spus serverul despre număr — sub prag, liber — pus sub câmpul lui, nu în toast. */
  const [numberError, setNumberError] = useState<string | null>(null);
  /** Se marchează după prima apăsare, nu în timp ce se completează. */
  const [submitted, setSubmitted] = useState(false);

  /**
   * Firma activă e **implicitul**, nu regula.
   *
   * Se pune o singură dată, când sesiunea sosește și operatorul n-a ales încă: un `useEffect`
   * care ar suprascrie de fiecare dată i-ar fi anulat alegerea la orice reîmprospătare a
   * sesiunii — exact felul de comportament care se descoperă abia când numărul e consumat.
   */
  useEffect(() => {
    const active = session.data?.activeCompany?.id;

    if (active !== undefined) {
      setCompanyId((current) => (current === '' ? active : current));
    }
  }, [session.data?.activeCompany?.id]);

  /**
   * Proprietarul actului: cel dat de ecran, altfel cel ales în câmp.
   *
   * Un singur loc care hotărăște, ca restul formularului să nu mai întrebe de unde vine.
   */
  const chosen: DocumentOwner | null =
    fixedOwner === null
      ? owner
      : fixedOwner.kind === 'customer'
        ? { kind: 'customer', customerId: fixedOwner.customerId, name: '' }
        : { kind: 'partner', partnerId: fixedOwner.partnerId, name: fixedOwner.partnerName };
  const problem = fileProblem(file);
  const manualNumber = /^[1-9][0-9]*$/.test(documentNumber) ? Number(documentNumber) : null;
  const numberFormatProblem =
    documentNumber !== '' && manualNumber === null
      ? 'Numărul trebuie să fie un întreg, de la 1.'
      : undefined;
  // Doar tipurile în uz: unul dezactivat rămâne pe actele emise cu el, dar serverul refuză
  // să mai primească altele (`document_type_inactive`).
  const availableTypes = (documentTypes.data ?? []).filter((type) => type.status);

  const mutation = useMutation({
    mutationFn: () => {
      if (file === null) {
        throw new Error('Niciun fișier ales.');
      }

      return uploadDocument({
        file,
        owner: uploadOwner(),
        documentTypeId,
        documentState,
        documentDate,
        notes,
        ...(manualNumber === null ? {} : { documentNumber: manualNumber }),
      });
    },
    onSuccess: async (document) => {
      toast.success(
        `Documentul a primit numărul ${document.documentNumber} în registrul ${document.companyName}.`,
      );
      await Promise.all([
        // Actul apare imediat în registru și în tabul clientului.
        queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY }),
        // Și pastila de „în așteptare" din meniu se recitește: n-are legătură cu încărcarea
        // de mână, dar ecranul de documente în așteptare stă alături și se deschide des.
        queryClient.invalidateQueries({ queryKey: INBOX_QUERY_KEY }),
      ]);

      setFile(null);
      // ⚠️ **Și mențiunea se golește.** Pagina de încărcare e drumul pentru un teanc de acte;
      // o notă rămasă în câmp s-ar fi lipit tăcut de următorul, care e al altui act.
      setNotes('');
      // Și numărul: lăsat în câmp, al doilea act din teanc ar fi primit `409` pe un număr
      // care tocmai s-a dat primului.
      setDocumentNumber('');
      setSubmitted(false);
      onUploaded?.(document);
    },
    onError: (error: unknown) => {
      if (
        error instanceof ApiError &&
        (error.code === 'document_number_taken' || error.code === 'document_number_out_of_range')
      ) {
        setNumberError(error.message);
        return;
      }

      // Mesajul serverului spune ce e de făcut — „clientul e arhivat", „tipul e dezactivat",
      // „fișierul trece de 25 MB". Un text inventat aici ar fi al doilea adevăr.
      toast.error(error instanceof ApiError ? error.message : 'Documentul nu a putut fi încărcat.');
    },
  });

  /**
   * Proprietarul, în forma pe care o cere clientul de API.
   *
   * Aruncă în loc să ghicească: `ready` a verificat deja ce trebuie completat, iar un
   * `customerId` gol strecurat până aici ar fi produs o cerere refuzată de server cu un
   * mesaj despre altceva.
   */
  function uploadOwner(): UploadOwner {
    if (chosen === null) {
      throw new Error('Niciun proprietar ales.');
    }

    if (chosen.kind === 'customer') {
      return { kind: 'customer', customerId: chosen.customerId };
    }

    if (companyId === '') {
      throw new Error('Firma lipsește.');
    }

    return { kind: 'partner', partnerId: chosen.partnerId, companyId };
  }

  /**
   * Firma căutată la ANAF devine partener **pe loc**.
   *
   * Cerut de fondator (2026-09-17): „caută la ANAF după CUI și îl creează pe loc". Fără asta,
   * operatorul cu un teanc de facturi de la furnizori ar fi trebuit să iasă din formular, să
   * adauge partenerul în Conta → Parteneri și să se întoarcă — pentru fiecare furnizor nou.
   *
   * ⚠️ **Nu creează un al doilea rând** dacă partenerul există: `POST /partners/ensure` caută
   * întâi după cod. Și nu-i suprascrie datele — o denumire corectată de mână rămâne corectată.
   *
   * ⚠️ **Partenerul se scrie înainte de document**, deci rămâne și dacă încărcarea eșuează.
   * E acceptat, și e alegerea corectă: un rând de partener e legitim și refolosibil, spre
   * deosebire de un număr de registru, care odată consumat nu se mai întoarce.
   */
  const lookupPartner = useMutation({
    mutationFn: async (found: CuiLookupCompany) => await ensurePartner(partnerInputFromAnaf(found)),
    onSuccess: async ({ partner, created }) => {
      // Partenerul găsit sau creat se alege singur în câmpul de sus — altfel operatorul ar fi
      // trebuit să-l caute imediat după ce l-a adus.
      setOwner({ kind: 'partner', partnerId: partner.id, name: partner.name });
      toast.success(
        created ? `Partener adăugat: ${partner.name}` : `Partener găsit: ${partner.name}`,
      );
      await queryClient.invalidateQueries({ queryKey: PARTNERS_QUERY_KEY });
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Partenerul nu a putut fi adăugat.');
    },
  });

  const ownerReady =
    chosen !== null &&
    // ⚠️ **Și firma**, la partener: fără ea serverul refuză, iar butonul ar fi trimis
    // operatorul într-un dus-întors cu fișierul de 25 MB cu tot.
    (chosen.kind === 'customer' || companyId !== '');

  const ready =
    file !== null &&
    problem === undefined &&
    ownerReady &&
    documentTypeId !== '' &&
    // Doar forma oprește butonul. Răspunsul serverului rămâne sub câmp, dar nu blochează: alt
    // client poate fi al altei firme, cu alt prag.
    numberFormatProblem === undefined;

  function incarca() {
    setSubmitted(true);

    if (!ready) {
      return;
    }

    mutation.mutate();
  }

  return (
    <div className="flex flex-col gap-y-4">
      {fixedOwner === null ? (
        <>
          {/*
            **Un singur câmp**, clienți și parteneri la un loc (#149). Ce era înainte —
            comutator + căutare + `Select` — cerea un răspuns înaintea întrebării: operatorul
            cu actul în mână știe denumirea, nu categoria celui care l-a trimis. Vezi
            `OwnerPicker`.
          */}
          <Field
            label="Al cui e actul"
            id="incarcare-proprietar"
            required
            error={submitted && owner === null ? 'Alege clientul sau partenerul.' : undefined}
          >
            <OwnerPicker
              id="incarcare-proprietar"
              value={owner}
              onChange={setOwner}
              disabled={mutation.isPending}
              invalid={submitted && owner === null}
            />
          </Field>

          {/*
            ⚠️ **Firma se alege doar la partener**, și doar după ce e ales unul. La un client
            ar fi fost o greșeală — vine de pe el; la partener nu există de unde s-o moștenească,
            iar `architecture/tenancy.md` interzice luarea ei tăcută din sesiune.
          */}
          {owner?.kind === 'partner' ? (
            <CompanyField
              companies={companies.data ?? []}
              value={companyId}
              onChange={setCompanyId}
              error={submitted && companyId === '' ? 'Alege firma.' : undefined}
              disabled={mutation.isPending}
            />
          ) : null}

          <Field label="Partener nou, după CUI" id="incarcare-partener-cui">
            <CuiLookup
              id="incarcare-partener-cui"
              value={partnerCui}
              onChange={setPartnerCui}
              onLookup={lookupCompanyByCui}
              onResult={(found) => lookupPartner.mutate(found)}
              disabled={mutation.isPending || lookupPartner.isPending}
            />
          </Field>
        </>
      ) : fixedOwner.kind === 'partner' ? (
        <>
          {/*
            Partenerul e dat de ecranul care a deschis sertarul — rândul din Conta →
            Parteneri. Se **scrie**, nu se ascunde: cine urcă un teanc trebuie să vadă pe
            cine îl pune, iar sertarul se deschide din rând, nu dintr-o listă.

            ⚠️ **Fără cod fiscal.** La un partener persoană fizică e un CNP, iar ecranul stă
            deschis cu actele pe masă. Vezi architecture/personal-data.md
          */}
          <Field label="Partener" id="incarcare-partener-fix">
            <Text id="incarcare-partener-fix" weight="plus">
              {fixedOwner.partnerName}
            </Text>
          </Field>

          <CompanyField
            companies={companies.data ?? []}
            value={companyId}
            onChange={setCompanyId}
            error={submitted && companyId === '' ? 'Alege firma.' : undefined}
            disabled={mutation.isPending}
          />
        </>
      ) : null}

      <Field
        label="Fișier"
        id="incarcare-fisier"
        required
        error={problem ?? (submitted && file === null ? 'Alege un fișier.' : undefined)}
        hint={`PDF sau poză, cel mult ${Math.round(MAX_DOCUMENT_FILE_BYTES / (1024 * 1024))} MB.`}
      >
        <Input
          id="incarcare-fisier"
          type="file"
          // `accept` e o comoditate — filtrează ce vede operatorul în dialogul de fișiere.
          // Ce contează se verifică pe server, pe conținutul primit.
          accept={DOCUMENT_FILE_ACCEPT}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          disabled={mutation.isPending}
        />
      </Field>

      <Field
        label="Tip document"
        id="incarcare-tip"
        required
        error={submitted && documentTypeId === '' ? 'Alege tipul.' : undefined}
      >
        <Select
          value={documentTypeId}
          onValueChange={setDocumentTypeId}
          disabled={mutation.isPending}
        >
          <Select.Trigger id="incarcare-tip">
            <Select.Value placeholder="Alege tipul" />
          </Select.Trigger>
          <Select.Content className="z-50">
            {availableTypes.map((type) => (
              <Select.Item key={type.id} value={type.id}>
                {type.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Field>

      <Field label="Stare" id="incarcare-stare">
        <Select
          value={String(documentState)}
          onValueChange={(value) => setDocumentState(Number(value) as 1 | 3)}
          disabled={mutation.isPending}
        >
          <Select.Trigger id="incarcare-stare">
            <Select.Value placeholder="Alege" />
          </Select.Trigger>
          <Select.Content className="z-50">
            {STATE_LIST.map((state) => (
              <Select.Item key={state.value} value={String(state.value)}>
                {state.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Field>

      <Field label="Data documentului" id="incarcare-data">
        <Input
          id="incarcare-data"
          type="date"
          value={documentDate}
          onChange={(event) => setDocumentDate(event.target.value)}
          disabled={mutation.isPending}
        />
      </Field>

      {/*
        Numărul de registru, opțional (#239): contractele aduse din sistemul vechi au deja unul,
        tipărit pe ele și pe facturi. Gol = următorul număr automat al firmei.
      */}
      <Field
        label="Număr de registru"
        id="incarcare-numar"
        error={numberFormatProblem ?? numberError ?? undefined}
        hint="Doar pentru acte din sistemul vechi, sub „Următorul număr automat” al firmei. Gol = numărul automat."
      >
        {/* `type="text"`, ca la seria de facturi: rotița mouse-ului nu schimbă numărul. */}
        <Input
          id="incarcare-numar"
          inputMode="numeric"
          value={documentNumber}
          placeholder="automat"
          onChange={(event) => {
            setDocumentNumber(event.target.value.trim());
            setNumberError(null);
          }}
          className="font-mono tabular-nums"
          disabled={mutation.isPending}
        />
      </Field>

      {/*
        Mențiunile, **ultimul câmp și opțional**: ce se completează obligatoriu stă deasupra.
        Cerut de fondator (2026-09-17) pentru teancul de facturi — „factura Orange, august",
        „adus de client la ghișeu".

        ⚠️ **Nu ajunge pe hârtie.** E o notă internă; ce se tipărește trebuie să fie câmp de
        template. Se poate rescrie după, de pe fișa documentului.
      */}
      <Field label="Mențiuni" id="incarcare-mentiuni">
        <Textarea
          id="incarcare-mentiuni"
          value={notes}
          rows={2}
          maxLength={DOCUMENT_NOTES_MAX_LENGTH}
          placeholder="ex. factura Orange, august"
          onChange={(event) => setNotes(event.target.value)}
          disabled={mutation.isPending}
        />
      </Field>

      <div className="flex justify-end gap-x-2">
        {onCancel ? (
          <Button variant="secondary" onClick={onCancel} disabled={mutation.isPending}>
            Renunță
          </Button>
        ) : null}
        <Button onClick={incarca} isLoading={mutation.isPending} disabled={mutation.isPending}>
          Încarcă și dă număr
        </Button>
      </div>
    </div>
  );
}

/**
 * Firma din registrul căreia iese numărul.
 *
 * Componentă, nu două copii ale aceluiași `Field`: se arată în două locuri — la partenerul
 * căutat în pagina de încărcare și la cel dat de rândul din Conta → Parteneri — iar o a doua
 * copie ar fi putut rămâne fără filtrul de firme active exact pe drumul mai nou.
 */
function CompanyField({
  companies,
  value,
  onChange,
  error,
  disabled,
}: {
  companies: readonly { id: string; status: boolean; name: string; shortName: string | null }[];
  value: string;
  onChange: (next: string) => void;
  error?: string | undefined;
  disabled: boolean;
}) {
  return (
    <Field label="Firma care dă numărul" id="incarcare-firma" required error={error}>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <Select.Trigger id="incarcare-firma">
          <Select.Value placeholder="Alege firma" />
        </Select.Trigger>
        <Select.Content className="z-50">
          {companies
            // Doar firmele active: una dezactivată nu mai emite, iar serverul o refuză
            // oricum (`document_company_inactive`).
            .filter((company) => company.status)
            .map((company) => (
              <Select.Item key={company.id} value={company.id}>
                {companyDisplayName(company)}
              </Select.Item>
            ))}
        </Select.Content>
      </Select>
    </Field>
  );
}

/**
 * Același formular, în sertar — pentru tabul „Documente" de pe fișa clientului și pentru
 * rândul de partener din Conta → Parteneri (#139).
 */
export function UploadDocumentDrawer({
  owner,
  open,
  onClose,
  onUploaded,
}: {
  owner: FixedUploadOwner;
  open: boolean;
  onClose: () => void;
  /** Ce face ecranul cu actul urcat. Lipsă = doar se închide sertarul. */
  onUploaded?: (document: DocumentListItem) => void;
}) {
  return (
    <Drawer open={open} onOpenChange={(next) => !next && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>
            {owner.kind === 'partner'
              ? `Încarcă document · ${owner.partnerName}`
              : 'Încarcă document'}
          </Drawer.Title>
        </Drawer.Header>

        <Drawer.Body className="overflow-y-auto">
          {/* ⚠️ **Randat condiționat, nu doar ascuns.** Închis, formularul se demontează, deci
              a doua deschidere pornește curat. Lăsat montat, ar fi păstrat fișierul ales data
              trecută într-un câmp care arată gol — iar apăsarea pe „Încarcă" ar fi urcat
              altceva decât ce vede operatorul. */}
          {open ? (
            <UploadDocumentForm
              fixedOwner={owner}
              onUploaded={(document) => {
                onUploaded?.(document);
                onClose();
              }}
              onCancel={onClose}
            />
          ) : null}
        </Drawer.Body>
      </Drawer.Content>
    </Drawer>
  );
}
